import type { RouteRecordRaw } from "vue-router"
import auth from "@/middlewares/auth"
import admin from "@/middlewares/admin"
import { useAccountStore } from "@/stores/account"
import { useAuthenticationStore } from "@/stores/authentication"
import { useEstablishmentStore } from "@/stores/establishment"
import { getAccessPlanCode, hasAnyPaidEstablishment } from "@/utils/contract"

import { PageTitleParamsType } from "@/types/Router.types"

const redirectToSettingsProfile = () => ({ name: "settings-profile" })

const isDemoEstablishment = () => {
  const establishmentStore = useEstablishmentStore()
  return getAccessPlanCode(establishmentStore.currentEstablishment) === "DEMO"
}

const hasPaidEstablishment = () => {
  return hasAnyPaidEstablishment(useAccountStore().establishments)
}

const canAccessPreferences = () => {
  const authenticationStore = useAuthenticationStore()
  return hasPaidEstablishment() && !authenticationStore.isSale
}

const canAccessAccountSettings = () => {
  const authenticationStore = useAuthenticationStore()
  return hasPaidEstablishment()
    && !authenticationStore.isSale
    && !authenticationStore.isPartnerAccount
    && !authenticationStore.isSaleUserAccountPartner
    && !authenticationStore.isEditor
}

const canAccessInvoicesSettings = () => {
  if (!canAccessAccountSettings()) {
    return false
  }
  const authenticationStore = useAuthenticationStore()
  const acc = authenticationStore.user?.account as { external_billing?: boolean } | undefined
  return acc?.external_billing !== true
}

const canAccessUsersManagement = () => {
  const accountStore = useAccountStore()
  const authenticationStore = useAuthenticationStore()
  return hasPaidEstablishment()
    && accountStore.hasPremium
    && !isDemoEstablishment()
    && !authenticationStore.isSale
    && !authenticationStore.isEditor
}

const canAccessReviewAiSettings = () => {
  const authenticationStore = useAuthenticationStore()
  return hasPaidEstablishment() && !authenticationStore.isSale && !authenticationStore.isEditor
}

const userRoutes: RouteRecordRaw[] = [
  {
    path: "/settings",
    component: () => import("@/views/settings/SettingsLayout.vue"),
    meta: {
      middleware: [auth],
      title: "profile.page_title",
    },
    children: [
      {
        path: "",
        redirect: { name: "settings-profile" },
      },
      {
        path: "profile",
        name: "settings-profile",
        component: () => import("@/views/settings/SettingsProfileSection.vue"),
        meta: {
          middleware: [auth],
          title: "profile.page_title",
        },
      },
      {
        path: "preferences",
        name: "settings-preferences",
        component: () => import("@/views/settings/SettingsPreferencesSection.vue"),
        meta: {
          middleware: [auth],
          title: "profile.page_title",
        },
        beforeEnter: () => {
          if (!canAccessPreferences()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
      {
        path: "billing",
        name: "settings-billing",
        component: () => import("@/views/settings/SettingsBillingSection.vue"),
        meta: {
          middleware: [auth],
          title: "profile.page_title",
        },
        beforeEnter: () => {
          if (!canAccessAccountSettings()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
      {
        path: "invoices",
        name: "settings-invoices",
        component: () => import("@/views/settings/SettingsInvoicesSection.vue"),
        meta: {
          middleware: [auth],
          title: "profile.page_title",
        },
        beforeEnter: () => {
          if (!canAccessInvoicesSettings()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
      {
        path: "payment-methods",
        name: "settings-payment-methods",
        component: () => import("@/views/settings/SettingsPaymentMethodsSection.vue"),
        meta: {
          middleware: [auth],
          title: "profile.page_title",
        },
        beforeEnter: () => {
          if (!canAccessAccountSettings()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
      {
        path: "users-management",
        name: "settings-users-management",
        component: () => import("@/views/settings/SettingsUsersManagementSection.vue"),
        meta: {
          middleware: [auth],
          title: "usermanagement.page_title",
        },
        beforeEnter: () => {
          if (!canAccessUsersManagement()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
      {
        path: "contracts-establishments",
        name: "settings-contracts-establishments",
        component: () => import("@/views/settings/SettingsContractsSection.vue"),
        meta: {
          middleware: [auth],
          title: "subscriptions.page_title",
        },
        beforeEnter: () => {
          const authenticationStore = useAuthenticationStore()
          if (authenticationStore.isSale) {
            return { name: "sales-contracts-management" }
          }
          if (authenticationStore.isEditor || !hasPaidEstablishment()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
      {
        path: "review-ai",
        name: "settings-review-ai",
        component: () => import("@/views/settings/SettingsReviewAiSection.vue"),
        meta: {
          middleware: [auth],
          title: "profile.page_title",
        },
        beforeEnter: () => {
          if (!canAccessReviewAiSettings()) {
            return redirectToSettingsProfile()
          }
          return true
        },
      },
    ],
  },
  {
    path: "/profile",
    name: "user-profil",
    redirect: { name: "settings-profile" },
  },
  {
    path: "/usermanagement",
    name: "user-management",
    redirect: { name: "settings-users-management" },
  },
  {
    path: "/usermanagement/user/:id?/:user?",
    name: "user-management-details",
    component: () => import("@/views/user/Create.vue"),
    meta: {
      middleware: [auth, admin],
      title: "usermanagement/user.page_title",
      hasTitleParams: {
        type: PageTitleParamsType.PARAM,
        name: "user",
      },
    },
  },
  {
    path: "/newuser",
    name: "user-create",
    component: () => import("@/views/user/Create.vue"),
    meta: {
      middleware: [auth, admin],
      title: "newuser.page_title",
    },
  },
]

export default userRoutes
